/**
 * Riding HUD (Bedrock/Java): while mounted the mount's health shows as a row of
 * vehicle hearts where the food bar sits (extra rows above for >10 hearts) and the
 * horse jump bar replaces the XP bar. Drawn in GUI-scaled stage coordinates.
 */
import type { GuiStage } from './kit';

const SPR = 'mc/gui/sprites/hud/';
const PIX = 'image-rendering:pixelated;pointer-events:none;background-size:100% 100%;background-repeat:no-repeat;position:absolute;';

export interface RideState { health: number; maxHealth: number; jump: number; }

export class RideHud {
  private readonly root = document.createElement('div');
  private readonly bg = document.createElement('div');
  private readonly fg = document.createElement('div');
  private readonly cool = document.createElement('div');
  private hearts: HTMLDivElement[] = [];
  private shown = false;
  private key = '';
  /** Last state drawn, for tests / diagnostics (null when not mounted). */
  state: RideState | null = null;

  constructor(private readonly stage: GuiStage) {
    this.root.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%;pointer-events:none;display:none;';
    this.bg.style.cssText = `${PIX}width:182px;height:5px;background-image:url(${SPR}jump_bar_background.png);`;
    this.fg.style.cssText = `${PIX}width:0;height:5px;background-size:182px 5px;background-image:url(${SPR}jump_bar_progress.png);`;
    this.cool.style.cssText = `${PIX}width:182px;height:5px;display:none;background-image:url(${SPR}jump_bar_cooldown.png);`;
    this.root.append(this.bg, this.fg, this.cool);
    stage.content.appendChild(this.root);
  }

  /** `s` null when not mounted; jump < 0 hides the jump bar (pig, strider). */
  update(s: RideState | null): void {
    this.state = s;
    const on = s !== null;
    if (on !== this.shown) { this.shown = on; this.root.style.display = on ? '' : 'none'; if (!on) this.key = ''; }
    if (!s) return;
    const W = this.stage.width, H = this.stage.height, cx = W / 2;
    const hasJump = s.jump >= 0;
    this.bg.style.display = this.fg.style.display = hasJump ? '' : 'none';
    if (hasJump) {
      this.bg.style.left = this.fg.style.left = this.cool.style.left = `${cx - 91}px`;
      this.bg.style.top = this.fg.style.top = this.cool.style.top = `${H - 29}px`;
      this.fg.style.width = `${Math.round(Math.min(1, s.jump) * 182)}px`;
    }
    const total = Math.max(1, Math.ceil(s.maxHealth / 2));
    const cur = Math.max(0, Math.ceil(s.health));
    const key = `${W},${H},${total},${cur}`;
    if (key === this.key) return;
    this.key = key;
    while (this.hearts.length < total) { const d = document.createElement('div'); d.style.cssText = `${PIX}width:9px;height:9px;`; this.root.appendChild(d); this.hearts.push(d); }
    for (let i = 0; i < this.hearts.length; i++) {
      const d = this.hearts[i];
      if (i >= total) { d.style.display = 'none'; continue; }
      d.style.display = '';
      const row = Math.floor(i / 10), col = i % 10;
      d.style.left = `${cx + 91 - col * 8 - 9}px`;
      d.style.top = `${H - 39 - row * 10}px`;
      const full = cur >= (i + 1) * 2, half = !full && cur === i * 2 + 1;
      const c = `url(${SPR}heart/vehicle_container.png)`;
      d.style.backgroundImage = full ? `url(${SPR}heart/vehicle_full.png), ${c}` : half ? `url(${SPR}heart/vehicle_half.png), ${c}` : c;
    }
  }
}
